import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Users, Share2, Flag, CheckCircle2, Lock, UserMinus, Trash2, Pencil, Heart, Send } from 'lucide-react';
import { useActivity } from '@hooks/useActivities';
import { useAuth } from '@context/AuthContext';
import { usePublicConfig } from '@context/PublicConfigContext';
import { resolveProfileDisplaySrc } from '@utils/profileImageSrc';
import Header from '@components/layout/Header';
import Avatar from '@components/ui/Avatar';
import CategoryBadge from '@components/ui/CategoryBadge';
import ActivitySafetyBadges from '@components/activity/ActivitySafetyBadges';
import StarRating from '@components/ui/StarRating';
import ChatBox from '@components/chat/ChatBox';
import FaceVerificationModal from '@components/safety/FaceVerificationModal';
import JoinLoginPromptModal from '@components/auth/JoinLoginPromptModal';
import Spinner from '@components/ui/Spinner';
import { format } from 'date-fns';
import {
  isActivityListingEnded,
  isActivityChatReadOnly,
  openGoogleMapsDirectionsToDestination,
  pluralNoun,
  combinedUserRatingPreview,
  formatYaarsInAndSpotsLeft,
} from '@utils/helpers';
import { buildActivityShareDetailLine } from '../../../shared/activityShareSummary';
import { clearPendingJoinIntent, readPendingJoinIntent, setPendingJoinIntent } from '@utils/pendingJoinIntent';
import api from '@utils/api';
import {
  trackActivityDelete,
  trackActivityJoin,
  trackActivityLeave,
} from '@utils/analytics';
import toast from 'react-hot-toast';
import RatingModal from '@components/ratings/RatingModal';
import { checkCanRate } from '@api/ratings';
import { useWishlist } from '@context/WishlistContext';
import ActivityMemoryPanel from '@components/activity/ActivityMemoryPanel';

const formatDetailWhenLine = (iso) => format(new Date(iso), 'EEE, MMM d · h:mm a');

const formatTimezoneSubtitle = (iso) => {
  const d = new Date(iso);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZone, timeZoneName: 'short' }).formatToParts(d);
    const shortName = parts.find((p) => p.type === 'timeZoneName')?.value?.trim() || '';
    return shortName ? `${shortName} · ${timeZone}` : timeZone;
  } catch {
    return timeZone;
  }
};

const RATING_MODAL_DISMISSED_PREFIX = 'playfynd_rating_modal_dismissed';

function ratingModalDismissedKey(userId, activityId) {
  return `${RATING_MODAL_DISMISSED_PREFIX}:${userId}:${activityId}`;
}

function readRatingModalDismissed(userId, activityId) {
  if (!userId || !activityId || typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(ratingModalDismissedKey(userId, activityId)) === '1';
  } catch {
    return false;
  }
}

function writeRatingModalDismissed(userId, activityId) {
  if (!userId || !activityId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ratingModalDismissedKey(userId, activityId), '1');
  } catch {
    // quota / private mode
  }
}

/** Diagonal top-left ribbon, clipped by a rounded image/card parent. */
const EndedCornerRibbon = ({ cancelled }) => (
  <div
    className="pointer-events-none absolute left-0 top-0 z-[21] h-[5.25rem] w-[5.25rem] overflow-hidden sm:h-[5.75rem] sm:w-[5.75rem]"
    aria-hidden
  >
    <div className="absolute left-[-2.35rem] top-[1.15rem] flex w-[9.25rem] -rotate-45 items-center justify-center bg-[rgb(255_0_0/0.95)] py-1.5 text-[10px] font-extrabold uppercase tracking-[0.2em] text-white shadow-md sm:left-[-2.5rem] sm:top-5 sm:w-[10rem]">
      <span className="select-none">{cancelled ? 'Cancelled' : 'Ended'}</span>
    </div>
  </div>
);

const ActivityDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { siteOrigin } = usePublicConfig();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const { activity, loading, error, refetch } = useActivity(id);
  const [joining, setJoining] = useState(false);
  const [removingParticipantId, setRemovingParticipantId] = useState(null);
  const [ratingTargets, setRatingTargets] = useState([]);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [verifyModal, setVerifyModal] = useState(false);
  const [verifyModalVariant, setVerifyModalVariant] = useState('join');
  const [joinLoginModal, setJoinLoginModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [participantToRemove, setParticipantToRemove] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const coverRefreshAttempted = useRef(false);
  const pendingAutoJoinAttemptedRef = useRef(false);
  const messagesSectionRef = useRef(null);
  const ratingsSectionRef = useRef(null);
  const memorySectionRef = useRef(null);

  useEffect(() => {
    coverRefreshAttempted.current = false;
    pendingAutoJoinAttemptedRef.current = false;
  }, [id]);

  /** Opened from Inbox with `#messages` — scroll to group chat (retries after ChatBox layout). */
  useEffect(() => {
    if (loading || error || !activity) return;
    if (location.hash !== '#messages') return;

    const scrollToMessages = () => {
      messagesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(scrollToMessages);
    });
    const t1 = setTimeout(scrollToMessages, 380);
    const t2 = setTimeout(scrollToMessages, 820);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [loading, error, activity?._id, location.hash, location.key, id]);

  /** Opened from Inbox with `#ratings` — scroll to host/participants and open rating flow when allowed. */
  useEffect(() => {
    if (loading || error || !activity) return;
    if (location.hash !== '#ratings') return;

    const scrollToRatings = () => {
      ratingsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(scrollToRatings);
    });
    const t1 = setTimeout(scrollToRatings, 380);
    const t2 = setTimeout(scrollToRatings, 820);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [loading, error, activity?._id, location.hash, location.key, id]);

  /** Opened from Notifications with `#memory` — scroll to post-vibe check-in. */
  useEffect(() => {
    if (loading || error || !activity) return;
    if (location.hash !== '#memory') return;

    const scrollToMemory = () => {
      memorySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(scrollToMemory);
    });
    const t1 = setTimeout(scrollToMemory, 380);
    const t2 = setTimeout(scrollToMemory, 820);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [loading, error, activity?._id, location.hash, location.key, id]);

  useEffect(() => {
    setShowRatingModal(false);
    setRatingTargets([]);
  }, [id]);

  useEffect(() => {
    if (loading || !activity || !user) return;
    const noCover = !activity.image || String(activity.image).trim() === '';
    const creatorId = activity.creator?._id || activity.creator;
    const isCreatorUser = creatorId && String(creatorId) === String(user._id);
    if (!noCover || !isCreatorUser || coverRefreshAttempted.current) return;
    coverRefreshAttempted.current = true;
    let cancelled = false;
    (async () => {
      try {
        await api.post(`/activities/${id}/refresh-cover`);
        await new Promise((r) => setTimeout(r, 2800));
        if (!cancelled) refetch();
      } catch {
        coverRefreshAttempted.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, activity, id, user, refetch]);

  useEffect(() => {
    const maybeOpenRating = async () => {
      if (!user || !id) return;
      if (location.hash !== '#ratings' && readRatingModalDismissed(user._id, id)) return;
      try {
        const { data } = await checkCanRate(id);
        if (data.canRate && data.pendingUsers?.length) {
          setRatingTargets(data.pendingUsers);
          setShowRatingModal(true);
        }
      } catch {
        // silent
      }
    };
    if (!loading && activity) {
      maybeOpenRating();
    }
  }, [loading, activity, id, user, location.hash]);

  useEffect(() => {
    if (!user || loading || !activity) return;
    if (pendingAutoJoinAttemptedRef.current) return;
    const pendingJoin = readPendingJoinIntent();
    if (!pendingJoin || String(pendingJoin.activityId) !== String(id)) return;
    pendingAutoJoinAttemptedRef.current = true;

    const run = async () => {
      const alreadyParticipant = activity.participants?.some((p) => (p._id || p) === user._id);
      if (alreadyParticipant || activity.status === 'cancelled' || isActivityListingEnded(activity, new Date())) {
        clearPendingJoinIntent();
        return;
      }
      if (activity.verifiedOnly && !user.faceVerified) return;
      try {
        setJoining(true);
        await api.post(`/activities/${id}/join`);
        trackActivityJoin({ activityId: id, category: activity.category });
        clearPendingJoinIntent();
        toast.success('You joined the vibe!');
        refetch();
      } catch (err) {
        const code = err.response?.data?.code;
        const msg = err.response?.data?.message || 'Failed to join';
        if (code === 'FACE_VERIFICATION_REQUIRED' || code === 'VERIFIED_ONLY_ACTIVITY') {
          setVerifyModalVariant(code === 'VERIFIED_ONLY_ACTIVITY' ? 'join' : 'generic');
          setVerifyModal(true);
          return;
        }
        clearPendingJoinIntent();
        toast.error(msg);
      } finally {
        setJoining(false);
      }
    };

    run();
  }, [activity, id, loading, refetch, user]);

  if (loading) return <Spinner className="min-h-screen" size="lg" />;

  if (error || !activity) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 bg-surface-50 dark:bg-surface-950">
        <div className="text-center">
          <div className="mb-4 mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-surface-100 dark:bg-surface-800">
            <MapPin size={28} className="text-surface-400" />
          </div>
          <p className="text-lg font-semibold text-surface-600 dark:text-surface-400 mb-2">Vibe not found</p>
          <p className="text-sm text-surface-400 dark:text-surface-500 mb-6">It may have been removed or the link is invalid.</p>
          <button onClick={() => navigate('/feed')} className="btn-primary">Back to Feed</button>
        </div>
      </div>
    );
  }

  const creatorId = activity.creator?._id || activity.creator;
  const organizerRatingPreview = combinedUserRatingPreview(activity.creator);
  const isParticipant = user && activity.participants?.some((p) => (p._id || p) === user._id);
  const isCreator = user && creatorId === user._id;
  const isActivityMember = Boolean(user && (isParticipant || isCreator));
  const isFull = activity.participants?.length >= activity.maxParticipants;
  const spotsLeft = activity.maxParticipants - (activity.participants?.length || 0);
  const wishlisted = isWishlisted(activity._id);

  const now = new Date();
  const activityEnded = isActivityListingEnded(activity, now);
  const chatReadOnly = isActivityChatReadOnly(activity, now);
  const endedStatusLabel = activity.status === 'cancelled' ? 'Cancelled' : 'Already Happened';
  const hasCoverImage = Boolean(activity.image && String(activity.image).trim() !== '');

  const canEdit =
    !activityEnded &&
    isCreator &&
    activity.status !== 'cancelled';

  const loc = activity.location;
  const locCoords = loc?.coordinates;
  const locLat = Array.isArray(locCoords) && locCoords.length === 2 ? Number(locCoords[1]) : null;
  const locLng = Array.isArray(locCoords) && locCoords.length === 2 ? Number(locCoords[0]) : null;
  const hasLocCoords = Number.isFinite(locLat) && Number.isFinite(locLng);
  const locAddress = typeof loc?.address === 'string' ? loc.address.trim() : '';
  const showLocationRow = Boolean(locAddress) || hasLocCoords;

  const handleJoin = async () => {
    if (!user) {
      setPendingJoinIntent({ activityId: id, path: `/activity/${id}` });
      setJoinLoginModal(true);
      return;
    }
    if (activity.verifiedOnly && !user.faceVerified) {
      setVerifyModalVariant('join');
      setVerifyModal(true);
      return;
    }
    setJoining(true);
    try {
      await api.post(`/activities/${id}/join`);
      trackActivityJoin({ activityId: id, category: activity.category });
      toast.success('You joined the vibe!');
      refetch();
    } catch (err) {
      const code = err.response?.data?.code;
      const msg = err.response?.data?.message || 'Failed to join';
      if (code === 'FACE_VERIFICATION_REQUIRED' || code === 'VERIFIED_ONLY_ACTIVITY') {
        setVerifyModalVariant(code === 'VERIFIED_ONLY_ACTIVITY' ? 'join' : 'generic');
        setVerifyModal(true);
      }
      toast.error(msg);
    } finally {
      setJoining(false);
    }
  };

  const handleLeave = async () => {
    setJoining(true);
    try {
      await api.post(`/activities/${id}/leave`);
      trackActivityLeave({ activityId: id, category: activity.category });
      toast.success('You left the vibe');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to leave');
    } finally {
      setJoining(false);
    }
  };

  const handleRemoveParticipant = (participant) => {
    setParticipantToRemove(participant);
    setRemoveConfirmOpen(true);
  };

  const handleConfirmRemoveParticipant = async () => {
    if (!participantToRemove) return;
    const pid = participantToRemove._id || participantToRemove;
    setRemovingParticipantId(pid);
    try {
      await api.delete(`/activities/${id}/participants/${pid}`);
      toast.success('Participant removed');
      setRemoveConfirmOpen(false);
      setParticipantToRemove(null);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not remove participant');
    } finally {
      setRemovingParticipantId(null);
    }
  };

  const handleDeleteActivity = () => {
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDeleteActivity = async () => {
    setDeleting(true);
    try {
      await api.delete(`/activities/${id}`);
      trackActivityDelete({ activityId: id, category: activity.category });
      toast.success('Vibe deleted');
      navigate('/feed', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete vibe');
    } finally {
      setDeleting(false);
    }
  };

  const handleShare = async () => {
    if (!activity) return;
    const shareUrl = `${window.location.origin}/activity/${id}`;
    const title = activity.title || 'Yaarvi vibe';
    const desc = (activity.description || '').trim();
    const line = `Join me for "${activity.title}" on Yaarvi`;
    const detailLine = buildActivityShareDetailLine(activity);
    const text = desc ? `${line}\n\n${desc}` : detailLine ? `${line}\n\n${detailLine}` : line;

    if (navigator.share) {
      try {
        const shareData = { title, text, url: shareUrl };
        const imageUrl = activity.image && String(activity.image).trim();
        if (imageUrl) {
          try {
            const res = await fetch(imageUrl, { mode: 'cors' });
            if (res.ok) {
              const blob = await res.blob();
              const type = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
              const ext = type.includes('png') ? 'png' : type.includes('webp') ? 'webp' : 'jpg';
              const file = new File([blob], `vibe-${id}.${ext}`, { type });
              const withFiles = { ...shareData, files: [file] };
              if (typeof navigator.canShare === 'function' && navigator.canShare(withFiles)) {
                await navigator.share(withFiles);
                return;
              }
            }
          } catch {
            // CORS, privacy, or target — fall back to text+url
          }
        }
        await navigator.share(shareData);
      } catch {
        // user cancelled share
      }
      return;
    }

    const clipboardText = `${text}\n\n${shareUrl}`;
    try {
      await navigator.clipboard.writeText(clipboardText);
      toast.success('Copied to clipboard');
    } catch {
      toast.success('Share this link: ' + shareUrl);
    }
  };

  const openProfile = (profileId) => {
    if (!profileId) return;
    navigate(`/profile/${profileId}`);
  };

  return (
    <div className="min-h-screen w-full bg-surface-50 dark:bg-surface-950">
      <Header rightAction={
        <div className="flex items-center gap-2">
          <button onClick={handleShare} className="theme-toggle" aria-label="Share vibe">
            <Share2 size={16} />
          </button>
          
        </div>
      } />

      <div className="w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-28 lg:pb-10 pt-6 space-y-5">
        {hasCoverImage && (
          <div className="relative overflow-hidden rounded-2xl ring-1 ring-black/5 dark:ring-white/10 h-44 sm:h-56">
            <img
              src={String(activity.image).trim()}
              alt=""
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
            <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-3">
              <span className="text-4xl sm:text-5xl drop-shadow-md opacity-90" aria-hidden>
                {activity.emoji}
              </span>
            </div>
            <button
              type="button"
              onClick={() => toggleWishlist(activity)}
              className="absolute top-3 right-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/80 shadow-sm backdrop-blur-sm transition-all duration-200 hover:scale-105 active:scale-95 dark:bg-black/40"
              aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <Heart size={18} className={wishlisted ? 'fill-red-500 text-red-500' : 'text-surface-700 dark:text-white/80'} />
            </button>
            {activityEnded && <EndedCornerRibbon cancelled={activity.status === 'cancelled'} />}
          </div>
        )}
        {/* Hero card */}
        <div className="card !p-6 sm:!p-8 relative overflow-hidden">
          {!hasCoverImage && activityEnded && (
            <EndedCornerRibbon cancelled={activity.status === 'cancelled'} />
          )}
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-brand-500/5 to-transparent rounded-bl-full dark:from-brand-500/10" />
          <div className="relative">
            <div className="flex items-start justify-between gap-3 mb-4">
              <h1 className="min-w-0 flex-1 text-xl font-bold tracking-tight text-surface-900 dark:text-white leading-snug sm:text-[1.35rem]">
                <span className="break-words">{activity.title}</span>
                {activity.emoji ? (
                  <span className="ml-1.5 inline-block align-middle text-[1.15em] leading-none" aria-hidden>
                    {activity.emoji}
                  </span>
                ) : null}
              </h1>
              <div className="shrink-0 pt-0.5">
                <CategoryBadge category={activity.category} size="sm" />
              </div>
            </div>
            <div className="mb-4">
              <ActivitySafetyBadges activity={activity} creator={activity.creator} />
            </div>

            {activity.description && (
              <p className="mb-6 text-sm sm:text-base leading-relaxed text-surface-500 dark:text-surface-400">
                {activity.description}
              </p>
            )}

            {activity.location?.exactLocationHidden && activity.verifiedOnly !== false && (
              <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                <Lock size={14} className="mt-0.5 shrink-0" />
                <span>Approximate area shown. Complete face verification to see the exact meeting point and full map details.</span>
              </div>
            )}
            <div className="space-y-3">
              {showLocationRow && (
                <div className="rounded-2xl bg-sky-50 px-4 py-4 sm:px-5 sm:py-5 dark:bg-sky-950/35">
                  <div className="flex gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/[0.06] dark:bg-surface-900 dark:ring-white/10">
                      <MapPin className="text-sky-600 dark:text-sky-400" size={22} strokeWidth={2.25} aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[0.9rem] font-bold leading-snug text-surface-900 dark:text-white">
                        {locAddress || (hasLocCoords ? 'Location on map' : 'Meeting location')}
                      </p>
                      <button
                        type="button"
                        onClick={() => openGoogleMapsDirectionsToDestination(loc)}
                        className="mt-2.5 inline-flex items-center justify-center gap-1.5 rounded-lg border border-violet-200/90 bg-white/80 px-2.5 py-1.5 text-[0.8125rem] font-semibold text-violet-700 shadow-sm transition-colors hover:border-violet-300 hover:bg-violet-50/90 active:scale-[0.98] dark:border-violet-500/25 dark:bg-surface-900/40 dark:text-violet-300 dark:hover:border-violet-400/40 dark:hover:bg-violet-950/35"
                        aria-label="Open directions in Google Maps"
                      >
                        <Send size={13} className="shrink-0 opacity-90" aria-hidden />
                        Directions
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activity.dateTime && (
                <div className="rounded-2xl bg-violet-50 px-4 py-4 sm:px-5 sm:py-5 dark:bg-violet-950/30">
                  <div className="flex gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/[0.06] dark:bg-surface-900 dark:ring-white/10">
                      <Clock className="text-blue-600 dark:text-blue-400" size={22} strokeWidth={2.25} aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[0.9rem] font-bold leading-snug text-surface-900 dark:text-white">
                        {formatDetailWhenLine(activity.dateTime)}
                      </p>
                      <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">
                        {formatTimezoneSubtitle(activity.dateTime)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-2xl bg-emerald-50 px-4 py-4 sm:px-5 sm:py-5 dark:bg-emerald-950/30">
                <div className="flex gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/[0.06] dark:bg-surface-900 dark:ring-white/10">
                    <Users className="text-emerald-600 dark:text-emerald-400" size={22} strokeWidth={2.25} aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[0.9rem] font-bold leading-snug text-surface-900 dark:text-white">
                      {formatYaarsInAndSpotsLeft(
                        activity.participants?.length || 0,
                        activity.maxParticipants,
                      )}
                      {spotsLeft > 0 && spotsLeft <= 2 && (
                        <span className="ml-2 text-xs font-semibold text-amber-600 dark:text-amber-400">Almost full</span>
                      )}
                      {isFull && (
                        <span className="ml-2 text-xs font-bold text-red-600 dark:text-red-400">Full</span>
                      )}
                    </p>
                    <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-white/80 dark:bg-emerald-950/50">
                      <div
                        className="h-full rounded-full bg-emerald-600 transition-all dark:bg-emerald-500"
                        style={{
                          width: `${Math.min(
                            100,
                            ((activity.participants?.length || 0) / Math.max(activity.maxParticipants, 1)) * 100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Organizer + Participants side by side on desktop */}
        <div
          ref={ratingsSectionRef}
          id="activity-ratings"
          className="grid scroll-mt-24 gap-4 sm:scroll-mt-28 sm:grid-cols-2"
        >
          {/* Organizer */}
          <button
            type="button"
            onClick={() => openProfile(creatorId)}
            className="card flex w-full items-center gap-3.5 text-left transition-colors hover:bg-surface-100/70 dark:hover:bg-surface-800/70"
          >
            <Avatar
              name={activity.creator?.name}
              src={resolveProfileDisplaySrc(activity.creator, siteOrigin)}
              size="md"
              verified={activity.creator?.faceVerified}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-surface-900 dark:text-white truncate">{activity.creator?.name}</span>
                {activity.creator?.faceVerified && (
                  <CheckCircle2
                    size={14}
                    className="text-brand-500 shrink-0"
                    title="Face verified"
                    aria-label="Face verified"
                  />
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <StarRating rating={organizerRatingPreview} size={12} />
                <span className="text-xs text-surface-500">
                  {organizerRatingPreview > 0 ? organizerRatingPreview.toFixed(1) : 'New'}
                </span>
              </div>
            </div>
            <span className="shrink-0 rounded-full bg-brand-50 px-3 py-1 text-xs font-bold text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
              Started by
            </span>
          </button>

          {/* Participants */}
          <div className="card">
            <h3 className="mb-3 text-xs font-semibold tracking-wide text-surface-500 dark:text-surface-400">
              {`${pluralNoun(activity.participants?.length || 0, 'participant', 'participants')} (${activity.participants?.length || 0})`}
            </h3>
            <div className="flex flex-wrap gap-2">
              {activity.participants?.map((p) => {
                const pid = p._id || p;
                const isOrganizerRow = pid === creatorId;
                const canRemove = isCreator && !isOrganizerRow && !activityEnded;
                const participantPreview = combinedUserRatingPreview(p);
                return (
                  <div
                    key={pid}
                    role="button"
                    tabIndex={0}
                    onClick={() => openProfile(pid)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openProfile(pid);
                      }
                    }}
                    className="flex items-start gap-1.5 rounded-xl bg-surface-50 py-2 pl-3 pr-1.5 text-left transition-colors hover:bg-surface-100 dark:bg-surface-800/50 dark:hover:bg-surface-800"
                  >
                    <Avatar name={p.name || 'User'} src={resolveProfileDisplaySrc(p, siteOrigin)} size="sm" />
                    <div className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-surface-700 dark:text-surface-300 max-w-[10rem] truncate">
                        {p.name || 'User'}
                      </span>
                      <div className="mt-0.5 flex items-center gap-1">
                        <StarRating rating={participantPreview} size={10} />
                        <span className="text-[10px] text-surface-500">
                          {participantPreview > 0 ? participantPreview.toFixed(1) : 'New'}
                        </span>
                      </div>
                    </div>
                    {canRemove && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveParticipant(p);
                        }}
                        disabled={!!removingParticipantId}
                        className="ml-0.5 mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center self-center rounded-lg text-red-500 transition-colors hover:bg-red-500/10 disabled:opacity-40"
                        aria-label={`Remove ${p.name || 'participant'} from vibe`}
                        title="Remove from vibe"
                      >
                        {removingParticipantId === pid ? (
                          <span className="text-[10px] font-bold">…</span>
                        ) : (
                          <UserMinus size={16} strokeWidth={2.25} />
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <ActivityMemoryPanel
          memorySectionRef={memorySectionRef}
          activityId={id}
          activityTitle={activity.title}
          activityDateTime={activity.dateTime}
          activityCancelled={activity.status === 'cancelled'}
          isParticipant={isActivityMember}
          user={user}
          siteOrigin={siteOrigin}
        />

        {/* Action */}
        {activityEnded ? (
          <div
            className="flex w-full items-center justify-center rounded-2xl border-2 border-surface-200 bg-surface-100/90 py-4 text-base font-bold tracking-wide text-surface-400 opacity-90 dark:border-surface-600/60 dark:bg-surface-800/50 dark:text-surface-500"
            role="status"
          >
            {endedStatusLabel}
          </div>
        ) : isCreator && activity.status !== 'cancelled' ? (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => navigate(`/activity/${id}/edit`)}
              disabled={!canEdit}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-brand-200 bg-brand-50 py-4 text-base font-bold text-brand-700 transition-all duration-300 hover:bg-brand-100 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-60 dark:border-brand-500/40 dark:bg-brand-500/10 dark:text-brand-300 dark:hover:bg-brand-500/20"
            >
              <Pencil size={18} strokeWidth={2.25} aria-hidden />
              Edit vibe
            </button>
            {!canEdit && (
              <p className="text-xs text-surface-500 dark:text-surface-400">
                Editing is available until the vibe ends.
              </p>
            )}
            <button
              type="button"
              onClick={handleDeleteActivity}
              disabled={deleting}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-red-200 bg-red-50 py-4 text-base font-bold text-red-600 transition-all duration-300 hover:bg-red-100 active:scale-[0.97] disabled:opacity-60 dark:border-red-800/50 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
            >
              <Trash2 size={18} strokeWidth={2.25} aria-hidden />
              {deleting ? 'Deleting...' : 'Delete vibe'}
            </button>
          </div>
        ) : !isCreator ? (
          isParticipant ? (
            <button
              onClick={handleLeave}
              disabled={joining}
              className="w-full rounded-2xl border-2 border-red-200 bg-red-50 py-4 text-base font-bold text-red-600 transition-all duration-300 hover:bg-red-100 active:scale-[0.97] dark:border-red-800/50 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
            >
              {joining ? 'Leaving...' : 'Leave the Vibe'}
            </button>
          ) : (
            <button
              onClick={handleJoin}
              disabled={joining || isFull}
              className="btn-primary w-full !py-4 !text-base"
            >
              {isFull ? 'Vibe Full' : joining ? 'Joining...' : 'Join the Vibe'}
            </button>
          )
        ) : null}

        <section
          id="activity-messages"
          ref={messagesSectionRef}
          className="scroll-mt-24 sm:scroll-mt-28"
          aria-label="Vibe messages"
        >
          {isParticipant && user && (
            <div>
              <h3 className="mb-3 text-xs font-semibold tracking-wide text-surface-500 dark:text-surface-400">
                Group Chat
              </h3>
              <ChatBox activityId={id} readOnly={chatReadOnly} />
            </div>
          )}
        </section>

        {/* Report */}
        {user && !isCreator && (
          <button className="btn-ghost w-full !text-xs !text-surface-400 gap-1.5 py-3">
            <Flag size={13} /> Report this vibe
          </button>
        )}

        {removeConfirmOpen && (
          <div
            className="fixed inset-0 z-[120] flex items-end justify-center bg-black/55 px-3 pb-[calc(env(safe-area-inset-bottom)+5.5rem)] pt-3 sm:items-center sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-participant-title"
          >
            <div className="w-full max-w-md overflow-hidden rounded-2xl border border-surface-200 bg-white shadow-2xl dark:border-surface-700 dark:bg-surface-900">
              <div className="border-b border-surface-200 px-4 py-3 dark:border-surface-800">
                <h3 id="remove-participant-title" className="text-base font-extrabold text-surface-900 dark:text-white">
                  {`Kick ${participantToRemove?.name || 'participant'} out of this plan?`}
                </h3>
                <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">
                  They’ll no longer be a part of this vibe and lose access to the chat.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => {
                    if (removingParticipantId) return;
                    setRemoveConfirmOpen(false);
                    setParticipantToRemove(null);
                  }}
                  disabled={!!removingParticipantId}
                  className="order-2 rounded-xl border border-surface-200 bg-white py-3 text-sm font-semibold text-surface-700 transition-colors hover:bg-surface-50 disabled:opacity-50 dark:border-surface-700 dark:bg-surface-900 dark:text-surface-200 dark:hover:bg-surface-800 sm:order-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmRemoveParticipant}
                  disabled={!!removingParticipantId}
                  className="order-1 rounded-xl bg-red-500 py-3 text-sm font-bold text-white transition-colors hover:bg-red-600 disabled:opacity-50 sm:order-2"
                >
                  {removingParticipantId ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteConfirmOpen && (
          <div
            className="fixed inset-0 z-[120] flex items-end justify-center bg-black/55 px-3 pb-[calc(env(safe-area-inset-bottom)+5.5rem)] pt-3 sm:items-center sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-activity-title"
          >
            <div className="w-full max-w-md overflow-hidden rounded-2xl border border-surface-200 bg-white shadow-2xl dark:border-surface-700 dark:bg-surface-900">
              <div className="border-b border-surface-200 px-4 py-3 dark:border-surface-800">
                <h3 id="delete-activity-title" className="text-base font-extrabold text-surface-900 dark:text-white">
                  Delete vibe?
                </h3>
                <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">
                  This will delete the vibe for everyone, remove it from the feed, and revoke participant chat access.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => {
                    if (deleting) return;
                    setDeleteConfirmOpen(false);
                  }}
                  disabled={deleting}
                  className="order-2 rounded-xl border border-surface-200 bg-white py-3 text-sm font-semibold text-surface-700 transition-colors hover:bg-surface-50 disabled:opacity-50 dark:border-surface-700 dark:bg-surface-900 dark:text-surface-200 dark:hover:bg-surface-800 sm:order-1"
                >
                  Keep vibe
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDeleteActivity}
                  disabled={deleting}
                  className="order-1 rounded-xl bg-red-500 py-3 text-sm font-bold text-white transition-colors hover:bg-red-600 disabled:opacity-50 sm:order-2"
                >
                  {deleting ? 'Deleting...' : 'Delete vibe'}
                </button>
              </div>
            </div>
          </div>
        )}

        <RatingModal
          isOpen={showRatingModal}
          activityId={id}
          targets={ratingTargets}
          onClose={(submitted) => {
            setShowRatingModal(false);
            if (submitted) {
              toast.success('Thanks for your feedback!');
              refetch();
            } else if (user && id) {
              writeRatingModalDismissed(user._id, id);
            }
          }}
        />

        <FaceVerificationModal
          open={verifyModal}
          onClose={() => setVerifyModal(false)}
          variant={verifyModalVariant}
        />

        <JoinLoginPromptModal open={joinLoginModal} onClose={() => setJoinLoginModal(false)} activityId={id} />
      </div>
    </div>
  );
};

export default ActivityDetailPage;
